import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { AuthContext } from '../App';
import { User, BookOpen, Plus, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import './Profile.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, setUser } = React.useContext(AuthContext);
  const [switching, setSwitching] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [quizData, setQuizData] = useState({
    title: '',
    difficulty: 'easy',
    subject: 'math',
    questions: [
      { question: '', options: ['', '', '', ''], correct_answer: '', type: 'multiple_choice' }
    ]
  });
  
  const switchRole = async () => {
    setSwitching(true);
    try {
      const response = await axios.post(`${API}/users/switch-role`, {}, { withCredentials: true });
      setUser({ ...user, role: response.data.role });
      toast.success(`Switched to ${response.data.role} mode!`);
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch role:', error);
      toast.error('Failed to switch role');
    } finally {
      setSwitching(false);
    }
  };
  
  const addQuestion = () => {
    setQuizData({
      ...quizData,
      questions: [
        ...quizData.questions,
        { question: '', options: ['', '', '', ''], correct_answer: '', type: 'multiple_choice' }
      ]
    });
  };
  
  const updateQuestion = (index, field, value) => {
    const newQuestions = [...quizData.questions];
    newQuestions[index][field] = value;
    setQuizData({ ...quizData, questions: newQuestions });
  };
  
  const updateOption = (qIndex, oIndex, value) => {
    const newQuestions = [...quizData.questions];
    newQuestions[qIndex].options[oIndex] = value;
    setQuizData({ ...quizData, questions: newQuestions });
  };
  
  const createQuiz = async () => {
    try {
      await axios.post(`${API}/quizzes`, quizData, { withCredentials: true });
      toast.success('Quiz created successfully!');
      setCreateDialogOpen(false);
      setQuizData({
        title: '',
        difficulty: 'easy',
        subject: 'math',
        questions: [
          { question: '', options: ['', '', '', ''], correct_answer: '', type: 'multiple_choice' }
        ]
      });
    } catch (error) {
      console.error('Failed to create quiz:', error);
      toast.error('Failed to create quiz');
    }
  };
  
  return (
    <div className="profile-page" data-testid="profile-page">
      <div className="profile-container">
        <motion.div
          className="page-header"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <User size={48} color="#FF6B9D" />
          <h1>Profile & Settings</h1>
        </motion.div>
        
        <div className="profile-card">
          <div className="profile-info">
            {user?.picture && (
              <img src={user.picture} alt={user.name} className="profile-avatar" />
            )}
            <div>
              <h2>{user?.name}</h2>
              <p className="profile-email">{user?.email}</p>
              <div className="role-badge" data-testid="user-role">
                {user?.is_premium ? '⭐ Premium Member' : '🆓 Free Plan'}
              </div>
            </div>
          </div>
          
        </div>
        
        <div className="teacher-section">
            <h3>🎓 Game Builders</h3>
            <p className="teacher-intro">Create amazing educational games! Click any builder below to get started.</p>
            
            <div className="teacher-actions">
              <div className="builders-grid">
                <div className="builder-card" onClick={() => navigate('/quiz-builder')} data-testid="go-to-builder-button">
                  <div className="builder-icon">📝</div>
                  <h4>Quiz Builder</h4>
                  <p>Create multiple-choice quizzes</p>
                  <div className="builder-btn">Create →</div>
                </div>
                
                <div className="builder-card" onClick={() => navigate('/puzzle-builder')}>
                  <div className="builder-icon">🧩</div>
                  <h4>Puzzle Builder</h4>
                  <p>Image reveal with questions</p>
                  <div className="builder-btn">Create →</div>
                </div>
                
                <div className="builder-card" onClick={() => navigate('/open-box-builder')}>
                  <div className="builder-icon">🎁</div>
                  <h4>Open Box Builder</h4>
                  <p>Gift boxes with surprises</p>
                  <div className="builder-btn">Create →</div>
                </div>
                
                <div className="builder-card" onClick={() => navigate('/flashcard-builder')}>
                  <div className="builder-icon">📚</div>
                  <h4>Flashcard Builder</h4>
                  <p>Flip cards for learning</p>
                  <div className="builder-btn">Create →</div>
                </div>
                
                <div className="builder-card" onClick={() => navigate('/anagram-builder')}>
                  <div className="builder-icon">🔤</div>
                  <h4>Anagram Builder</h4>
                  <p>Word building game</p>
                  <div className="builder-btn">Create →</div>
                </div>
              </div>
              
              <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <button className="btn btn-primary create-btn quick-create-btn" data-testid="create-quiz-button">
                  <Plus size={20} />Quick Quiz Creator (Legacy)
                </button>
              </DialogTrigger>
                <DialogContent className="quiz-creator-dialog">
                  <DialogHeader>
                    <DialogTitle>Create New Quiz</DialogTitle>
                  </DialogHeader>
                  
                  <div className="quiz-form">
                    <div className="form-group">
                      <Label htmlFor="title">Quiz Title</Label>
                      <Input
                        id="title"
                        value={quizData.title}
                        onChange={(e) => setQuizData({ ...quizData, title: e.target.value })}
                        placeholder="e.g., Math Challenge"
                      />
                    </div>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <Label htmlFor="difficulty">Difficulty</Label>
                        <Select
                          value={quizData.difficulty}
                          onValueChange={(value) => setQuizData({ ...quizData, difficulty: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="easy">Easy</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="hard">Hard</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="form-group">
                        <Label htmlFor="subject">Subject</Label>
                        <Select
                          value={quizData.subject}
                          onValueChange={(value) => setQuizData({ ...quizData, subject: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="math">Math</SelectItem>
                            <SelectItem value="science">Science</SelectItem>
                            <SelectItem value="general">General</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="questions-section">
                      <h4>Questions</h4>
                      {quizData.questions.map((q, qIndex) => (
                        <div key={qIndex} className="question-form">
                          <Label>Question {qIndex + 1}</Label>
                          <Input
                            value={q.question}
                            onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                            placeholder="Enter question"
                          />
                          
                          <div className="options-grid">
                            {q.options.map((opt, oIndex) => (
                              <Input
                                key={oIndex}
                                value={opt}
                                onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                                placeholder={`Option ${oIndex + 1}`}
                              />
                            ))}
                          </div>
                          
                          <Label>Correct Answer</Label>
                          <Input
                            value={q.correct_answer}
                            onChange={(e) => updateQuestion(qIndex, 'correct_answer', e.target.value)}
                            placeholder="Enter the correct answer"
                          />
                        </div>
                      ))}
                      
                      <button onClick={addQuestion} className="btn btn-secondary add-question-btn">
                        <Plus size={16} />
                        Add Question
                      </button>
                    </div>
                    
                    <button onClick={createQuiz} className="btn btn-primary">
                      Create Quiz
                    </button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
      </div>
    </div>
  );
}

export default Profile;
